import React, { useState, useEffect } from 'react';
import { getTipoDocumentoById, createTipoDocumento, updateTipoDocumento } from '../services/tipoDocumentoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function TipoDocumentoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [categorias, setCategorias] = useState([]);
    const [formData, setFormData] = useState({
        nombreTipo: '',
        descripcion: '',
        categoriaDocumento: null,
        activo: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCategorias();
        if (isEditMode) {
            cargarTipoDocumento();
        }
    }, [id]);

    const fetchCategorias = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/categorias-documento`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setCategorias(data);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
            toast.error('No se pudieron cargar las categorías de documento');
        }
    };

    const cargarTipoDocumento = async () => {
        try {
            setLoading(true);
            const data = await getTipoDocumentoById(token, id);
            setFormData({
                nombreTipo: data.nombreTipo,
                descripcion: data.descripcion || '',
                categoriaDocumento: data.categoriaDocumento || null,
                activo: data.activo !== undefined ? data.activo : true
            });
        } catch (error) {
            console.error('Error al cargar el tipo de documento:', error);
            toast.error('Error al cargar los datos del tipo de documento');
            navigate('/parametrizacion/tipos-documento');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombreTipo.trim()) {
            toast.error('El nombre del tipo es requerido');
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateTipoDocumento(token, id, formData);
                toast.success('Tipo de documento actualizado exitosamente');
            } else {
                await createTipoDocumento(token, formData);
                toast.success('Tipo de documento creado exitosamente');
            }
            setTimeout(() => navigate('/parametrizacion/tipos-documento'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error(error.message || 'Error al guardar el tipo de documento');
        } finally {
            setLoading(false);
        }
    };

    const handleCategoriaChange = (e) => {
        const catId = e.target.value;
        if (!catId) {
            setFormData({ ...formData, categoriaDocumento: null });
        } else {
            const cat = categorias.find(c => c.id === parseInt(catId));
            setFormData({ ...formData, categoriaDocumento: cat || null });
        }
    };

    return (
        <div className="max-w-3xl mx-auto bg-white shadow-sm border border-gray-200 mb-6">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex items-center gap-3 text-white">
                <button
                    onClick={() => navigate('/parametrizacion/tipos-documento')}
                    className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                    title="Volver a la lista"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-300" />
                    <h1 className="text-lg font-semibold tracking-tight">
                        {isEditMode ? 'Mantenimiento: Editar Tipo de Documento' : 'Mantenimiento: Nuevo Tipo de Documento'}
                    </h1>
                </div>
            </div>

            {/* Form */}
            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                                Nombre del Tipo <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                maxLength="50"
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                placeholder="Ej: Cédula de Identidad, Pasaporte"
                                value={formData.nombreTipo}
                                onChange={(e) => setFormData({ ...formData, nombreTipo: e.target.value })}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                                Descripción <span className="text-gray-400 font-normal lowercase">(opcional)</span>
                            </label>
                            <input
                                type="text"
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                placeholder="Breve descripción del documento"
                                value={formData.descripcion}
                                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                                Categoría de Documento
                            </label>
                            <select
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                                value={formData.categoriaDocumento ? formData.categoriaDocumento.id : ''}
                                onChange={handleCategoriaChange}
                            >
                                <option value="">Sin categoría asignada</option>
                                {categorias.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.nombre} ({cat.valor})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-sm border border-gray-200 mt-4">
                        <div>
                            <p className="font-bold text-sm text-gray-700 uppercase">Estado del Tipo de Documento</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {formData.activo ? 'El tipo está disponible para ser seleccionado.' : 'El tipo no aparecerá en las listas de selección.'}
                            </p>
                        </div>
                        <label className="flex items-center cursor-pointer">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={formData.activo}
                                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                                />
                                <div className={`block w-12 h-6 rounded-full transition-colors ${formData.activo ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                                <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.activo ? 'transform translate-x-6' : ''}`}></div>
                            </div>
                        </label>
                    </div>

                    <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/tipos-documento')}
                            className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors flex items-center text-sm font-medium"
                        >
                            <X className="w-3.5 h-3.5 mr-1.5" />
                            CANCELAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors flex items-center text-sm font-medium disabled:opacity-70 shadow-sm"
                        >
                            {loading ? (
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <Save className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            {isEditMode ? 'GUARDAR CAMBIOS' : 'CREAR TIPO DE DOCUMENTO'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}